import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { IconButton, makeStyles, Typography } from '@material-ui/core';
import { KeyboardArrowDownOutlined } from '@material-ui/icons';
import clsx from 'clsx';
import { forwardRef, useState } from 'react';
import { transitionSpeed } from '../../theme';
const useStyles = makeStyles((theme) => ({
    root: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 6,
        height: '100%',
        overflow: 'hidden',
        '& .ag-overlay-loading-center': {
            border: 0,
        },
        '& .ag-root-wrapper.ag-ltr': {
            border: 0,
        },
    },
    container: {
        display: 'block',
    },
    containerCollapsed: {
        display: 'none',
    },
    containerWithPadding: {
        padding: theme.spacing(3, 3, 2),
    },
    header: {
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        minHeight: 56,
        padding: theme.spacing(0, 3),
    },
    headerCollapsed: {
        borderBottom: 0,
        minHeight: 55,
    },
    headerContent: {
        display: 'flex',
        flex: '1 1 auto',
    },
    headerCopy: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    toggle: {
        marginRight: theme.spacing(1),
        transition: `transform ${transitionSpeed} ease-out`,
    },
    toggled: {
        transform: 'rotate(-90deg)',
    },
}));
/**
 * DataCard component renders a card with a header and content.
 *
 * @param {React.ReactNode} [props.actions] - Optional actions to be displayed in the header.
 * @param {React.ReactNode} props.children - The content to be displayed inside the card.
 * @param {string} props.className - Optional CSS class name that is added to the root container.
 * @param {string} props.dataTestId - Optional test attribute.
 * @param {string} props.heading - The main heading text for the card.
 * @param {string} [props.enablePadding] - Optional whether or not the content in the container should have padding
 * @param {string} [props.description] - Optional description text for the card.
 * @param {string} [props.isCollapsed] - Optional whether or not to collapse container by default.
 * @param {string} [props.isCollapsible] - Optional to enable collapse functionality.
 *
 * @returns {JSX.Element} The rendered DataCard component.
 */
export const DataCard = forwardRef(({ actions, children, className, dataTestId = '', description, enablePadding = false, heading, isCollapsed = false, isCollapsible = false, }, ref) => {
    const classes = useStyles();
    const [isToggled, setIsToggled] = useState(isCollapsed && isCollapsible);
    const onHandleToggleClick = () => {
        setIsToggled(!isToggled);
    };
    return (_jsxs("div", { className: clsx(classes.root, className), "data-testid": dataTestId, ref: ref, children: [_jsxs("div", { className: clsx(classes.header, {
                    [classes.headerCollapsed]: isToggled,
                }), children: [_jsxs("div", { className: classes.headerContent, children: [isCollapsible && (_jsx(IconButton, { "aria-label": 'Toggle card visibility', className: clsx(classes.toggle, {
                                    [classes.toggled]: isToggled,
                                }), color: 'secondary', onClick: onHandleToggleClick, children: _jsx(KeyboardArrowDownOutlined, {}) })), _jsxs("div", { className: classes.headerCopy, children: [_jsx(Typography, { color: 'textPrimary', variant: 'h3', children: heading }), description && (_jsx(Typography, { color: 'textSecondary', variant: 'body2', children: description }))] })] }), actions && actions] }), _jsx("div", { className: clsx(classes.container, {
                    [classes.containerCollapsed]: isToggled,
                    [classes.containerWithPadding]: enablePadding,
                }), children: children })] }));
});
